// Captura DOM de um container e gera JSON no formato do mockup-builder.html.
// Usado pelo modo Alt+T (admin only). Output: cole `?import=<base64>` na URL do builder
// pra abrir com a estrutura ja carregada.

interface MockupElement {
  id: number;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
  fontSize?: number;
  bold?: boolean;
  // Cores reais — preserva visual da tela capturada. Aplicadas inline no builder.
  bgColor?: string;
  textColor?: string;
}

interface MockupPayload {
  elements: MockupElement[];
  idCounter: number;
  // Dimensoes do container capturado — usado pra redimensionar o canvas do builder
  // pra largura/altura fieis a tela real (default fixo de 1200px nao cabe paineis largos).
  containerWidth?: number;
  containerHeight?: number;
  // Background da pagina (body/html) — aplicado ao canvas do builder pra refletir
  // gradient/cor de fundo da tela real, nao so dos cards.
  pageBackground?: string;
}

// Tipos suportados pelo mockup-builder (DEFAULTS keys).
type MockupTipo =
  | 'title'
  | 'subtitle'
  | 'label'
  | 'input'
  | 'textarea'
  | 'select'
  | 'checkbox'
  | 'radio'
  | 'button'
  | 'button-secondary'
  | 'button-success'
  | 'button-danger'
  | 'table'
  | 'listbox'
  | 'panel'
  | 'divider'
  | 'image'
  | 'badge'
  | 'tabs';

// Normaliza cor RGB pra detectar variantes de botao (verde/vermelho/cinza/azul).
function normalizarBotao(el: HTMLElement): MockupTipo {
  const cs = window.getComputedStyle(el);
  const bg = cs.backgroundColor || '';
  // RGB strings
  const rgb = bg.match(/\d+/g)?.map(Number) ?? [];
  const [r = 0, g = 0, b = 0] = rgb;

  // Vermelho dominante (#dc2626, #b91c1c, #ef4444)
  if (r > 180 && g < 80 && b < 80) return 'button-danger';
  // Verde dominante (#16a34a, #15803d, #22c55e)
  if (g > 140 && r < 100 && b < 130) return 'button-success';
  // Botao secundario (cinza claro / branco / transparente)
  if (el.classList.contains('btn-secondary')) return 'button-secondary';
  if (el.classList.contains('btn-secundario')) return 'button-secondary';
  // Cinza/branco como cor de fundo
  if (r > 200 && g > 200 && b > 200) return 'button-secondary';
  // Default: azul/primario
  return 'button';
}

// Verifica se um <label> envolve um campo (caso comum: <label>Tipo<select>...</select></label>).
// Nesse caso, queremos capturar o label como TEXTO PURO + descer no campo, em vez de virar
// um label com textContent inteiro (que junta texto do select tambem).
function labelEnvolveCampo(el: HTMLElement): boolean {
  if (el.tagName.toLowerCase() !== 'label') return false;
  return !!el.querySelector('input:not([type="radio"]):not([type="checkbox"]), select, textarea');
}

// <label><input type="radio|checkbox"/> texto/span</label> — capturar como radio/checkbox unico,
// com texto = textContent inteiro do label (sem descer).
function labelDeRadioOuCheckbox(el: HTMLElement): 'radio' | 'checkbox' | null {
  if (el.tagName.toLowerCase() !== 'label') return null;
  const inp = el.querySelector('input[type="radio"], input[type="checkbox"]') as HTMLInputElement | null;
  if (!inp) return null;
  return inp.type as 'radio' | 'checkbox';
}

// Container de abas: tem class .abas OU >= 2 filhos diretos .aba-btn / [role=tab]
function ehContainerAbas(el: HTMLElement): boolean {
  if (el.classList.contains('abas')) return true;
  if (el.getAttribute('role') === 'tablist') return true;
  const filhos = Array.from(el.children).filter((c) => c instanceof HTMLElement) as HTMLElement[];
  if (filhos.length < 2) return false;
  const todasAbas = filhos.every(
    (f) => f.classList.contains('aba-btn') || f.getAttribute('role') === 'tab',
  );
  return todasAbas;
}

// Texto das abas: cada label de aba separado por virgula
function textoAbas(el: HTMLElement): string {
  const btns = Array.from(el.querySelectorAll('.aba-btn, [role="tab"], button')) as HTMLElement[];
  return btns
    .map((b) => (b.textContent || '').trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .join(',') || 'Aba 1,Aba 2';
}

// Texto solto: <span> ou <strong> com texto curto, fora de elementos ja semanticos.
// Captura "Cod 0218", "ADRIANO ENGENHEIRO", "0 itens — R$ 0,00" — info inline que sumiria.
function ehTextoIsolado(el: HTMLElement): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag !== 'span' && tag !== 'strong') return false;
  // Ignora se ja tem fundo (badge) ou tem filhos com tags semanticas
  if (el.querySelector('input, select, textarea, button, svg, img')) return false;
  const txt = (el.textContent || '').trim();
  if (txt.length < 2 || txt.length > 200) return false;
  return true;
}

// <div> sem filhos elementos, so texto — captura como label.
// Pega "Devolvido", "R$ 0,00", "Levado", "Saldo", "Nenhum item adicionado" — leaf texts.
function ehDivTextoLeaf(el: HTMLElement): boolean {
  if (el.tagName.toLowerCase() !== 'div') return false;
  // Sem filhos do tipo Element (so text nodes)
  if (el.children.length > 0) return false;
  const txt = (el.textContent || '').trim();
  return txt.length >= 1 && txt.length <= 120;
}

// <div> com fundo (cor ou gradient) + visual de "card" (border ou borderRadius). BFS desce.
function ehPainelComFundo(el: HTMLElement): boolean {
  if (el.tagName.toLowerCase() !== 'div') return false;
  if (el.offsetWidth < 120 || el.offsetHeight < 40) return false;
  const cs = window.getComputedStyle(el);
  const bg = cs.backgroundColor || '';
  const img = cs.backgroundImage || '';
  const temBg = bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent';
  const temImg = img && img !== 'none';
  if (!temBg && !temImg) return false;
  // Precisa ter borderRadius OU border solido — evita capturar app-content/root inteiro
  const radius = parseFloat(cs.borderTopLeftRadius || '0');
  const hasBorder = cs.borderStyle && cs.borderStyle !== 'none';
  if (radius < 3 && !hasBorder) return false;
  return true;
}

// Pega so os textos diretos do elemento (TextNodes filhos imediatos), ignorando filhos.
function textoDireto(el: HTMLElement): string {
  let txt = '';
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      txt += (node.textContent || '') + ' ';
    }
  }
  return txt.trim().replace(/\s+/g, ' ');
}

// Detecta o tipo do mockup a partir do elemento DOM.
function tipoDoElemento(el: HTMLElement): MockupTipo | null {
  const tag = el.tagName.toLowerCase();

  if (tag === 'h1') return 'title';
  if (tag === 'h2' || tag === 'h3') return 'subtitle';
  if (tag === 'label') {
    // <label><input type="radio|checkbox"/> texto</label> — capturar como radio/checkbox unico
    const radioOuCheck = labelDeRadioOuCheckbox(el);
    if (radioOuCheck) return radioOuCheck;
    // <label>Texto<input/select/textarea/></label> — trata como "label" mas BFS continuara
    // descendo pra capturar o campo aninhado tambem (ver loop principal).
    return 'label';
  }
  if (tag === 'textarea') return 'textarea';
  if (tag === 'select') return 'select';

  if (tag === 'input') {
    const t = (el as HTMLInputElement).type;
    if (t === 'checkbox') return 'checkbox';
    if (t === 'radio') return 'radio';
    return 'input';
  }

  if (tag === 'button') return normalizarBotao(el);

  if (tag === 'table') return 'table';
  if (tag === 'img') return 'image';
  if (tag === 'hr') return 'divider';

  // Container de abas: detecta antes de span pra capturar como tabs unico
  if (tag === 'div' && ehContainerAbas(el)) return 'tabs';

  // Badge: span pequeno com background colorido
  if (tag === 'span') {
    const cs = window.getComputedStyle(el);
    const bg = cs.backgroundColor || '';
    if (bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      const w = el.offsetWidth;
      if (w < 80) return 'badge';
    }
  }

  // Texto solto (<span>/<strong> com texto curto) — captura como label.
  if (ehTextoIsolado(el)) return 'label';

  // <div> leaf so com texto — captura como label
  if (ehDivTextoLeaf(el)) return 'label';

  // <div> com background colorido (cards, paineis) — vira panel, e BFS continua descendo
  if (ehPainelComFundo(el)) return 'panel';

  return null;
}

// Texto representativo de um elemento (label, value, placeholder, ou texto interno).
function textoDoElemento(el: HTMLElement, tipo: MockupTipo): string {
  // Caso especial: label que envolve campo. Pega so o texto direto.
  if (tipo === 'label' && labelEnvolveCampo(el)) {
    return textoDireto(el) || '?';
  }
  // Tabs: junta labels das abas
  if (tipo === 'tabs') {
    return textoAbas(el);
  }
  // Botao sem texto (so icone svg): cair em title/aria-label
  if (tipo === 'button' || tipo === 'button-secondary' || tipo === 'button-success' || tipo === 'button-danger') {
    const txt = (el.textContent || '').trim();
    if (txt) return txt.slice(0, 40);
    const fallback = el.getAttribute('title') || el.getAttribute('aria-label') || '';
    if (fallback) return fallback.slice(0, 40);
    return '×';
  }
  if (tipo === 'input' || tipo === 'textarea') {
    const inp = el as HTMLInputElement;
    if (inp.value) return inp.value;
    if (inp.placeholder) return inp.placeholder;
    return '...';
  }
  if (tipo === 'select') {
    const sel = el as HTMLSelectElement;
    const opt = sel.options[sel.selectedIndex];
    return opt?.text ?? 'Selecione...';
  }
  if (tipo === 'table') {
    // Capturar headers + algumas linhas
    const tabela = el as HTMLTableElement;
    const linhas: string[] = [];
    const heads = Array.from(tabela.querySelectorAll('thead th, thead td')).map(
      (th) => (th.textContent || '').trim() || 'Col',
    );
    if (heads.length > 0) linhas.push(heads.join(','));
    const tbodyRows = Array.from(tabela.querySelectorAll('tbody tr')).slice(0, 3);
    for (const tr of tbodyRows) {
      const cels = Array.from(tr.querySelectorAll('td')).map(
        (td) => (td.textContent || '').trim().slice(0, 30) || '—',
      );
      if (cels.length > 0) linhas.push(cels.join(','));
    }
    return linhas.join('\n') || 'Tabela';
  }
  if (tipo === 'image') {
    const img = el as HTMLImageElement;
    return img.alt || 'Imagem';
  }
  // label, title, subtitle, button, badge, checkbox, radio
  const txt = (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 80);
  return txt || '?';
}

// Cor/imagem de fundo. Aplicada a panel/badge. Preserva gradient (background-image) tambem
// — temas customizados podem usar gradient em --card-bg, e nesse caso backgroundColor vem
// transparente. Combina cor + gradient se ambos existirem.
function corDeFundoSeAplicavel(el: HTMLElement): string | undefined {
  const cs = window.getComputedStyle(el);
  const bg = cs.backgroundColor || '';
  const img = cs.backgroundImage || '';
  const temBg = bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent';
  const temImg = img && img !== 'none';
  if (!temBg && !temImg) return undefined;
  if (temImg && temBg) return `${bg} ${img}`;
  return temImg ? img : bg;
}

// Cor do texto (so se nao for o cinza-escuro/preto default). Aplicada a label/title/subtitle.
function corDeTextoSeAplicavel(el: HTMLElement): string | undefined {
  const cs = window.getComputedStyle(el);
  const color = cs.color || '';
  if (!color) return undefined;
  const rgb = color.match(/\d+/g)?.map(Number) ?? [];
  if (rgb.length < 3) return undefined;
  // Pular pretos/cinzas-escuros padrao (rgb < 80 em todas) — defaults do mockup ja cobrem
  if (rgb[0]! < 80 && rgb[1]! < 80 && rgb[2]! < 80) return undefined;
  return color;
}

function fontSizeNum(el: HTMLElement): number {
  const fs = window.getComputedStyle(el).fontSize;
  const n = parseFloat(fs);
  return Number.isFinite(n) ? Math.round(n) : 14;
}

function isBold(el: HTMLElement): boolean {
  const fw = window.getComputedStyle(el).fontWeight;
  const n = parseInt(fw, 10);
  return Number.isFinite(n) && n >= 600;
}

// Verifica se o elemento e visivel (tem dimensoes > 0 e display nao-none).
function isVisivel(el: HTMLElement): boolean {
  if (el.offsetWidth === 0 || el.offsetHeight === 0) return false;
  const cs = window.getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden') return false;
  return true;
}

// Percorre o DOM dentro do container e captura todos os elementos relevantes.
// Coordenadas ficam relativas ao container (subtrai offset).
export function capturarParaMockup(container: HTMLElement): MockupPayload {
  const elementos: MockupElement[] = [];
  const idCounter = { v: 0 };

  const containerRect = container.getBoundingClientRect();

  // BFS do DOM. Quando achamos um elemento mapeavel, capturamos e NAO descemos nele
  // (evita capturar texto interno de label como label separado, button text como label, etc).
  const fila: HTMLElement[] = [container];
  const seen = new WeakSet<HTMLElement>();

  while (fila.length > 0) {
    const cur = fila.shift()!;
    if (seen.has(cur)) continue;
    seen.add(cur);

    if (cur !== container) {
      if (!isVisivel(cur)) continue;
      const tipo = tipoDoElemento(cur);
      if (tipo) {
        const rect = cur.getBoundingClientRect();
        const x = Math.round(rect.left - containerRect.left);
        const y = Math.round(rect.top - containerRect.top);
        const w = Math.round(rect.width);
        const h = Math.round(rect.height);
        // Filtra elementos exageradamente pequenos ou fora da area do container
        if (w < 4 || h < 4) continue;
        if (x + w < 0 || y + h < 0) continue;

        const text = textoDoElemento(cur, tipo);
        const elFontSize = fontSizeNum(cur);
        const bold = isBold(cur);

        // Se o label envolve um campo, ajusta altura pra so a parte do texto direto
        // (evita label com altura de label+input). Default ~20px do mockup-builder.
        const isLabelWrapper = tipo === 'label' && labelEnvolveCampo(cur);
        const wAdj = isLabelWrapper ? Math.min(w, 200) : w;
        const hAdj = isLabelWrapper ? 20 : h;

        const elem: MockupElement = {
          id: ++idCounter.v,
          type: tipo,
          x,
          y,
          w: wAdj,
          h: hAdj,
          text,
          ...(elFontSize !== 14 ? { fontSize: elFontSize } : {}),
          ...(bold ? { bold: true } : {}),
        };
        if (tipo === 'panel' || tipo === 'badge') {
          const bg = corDeFundoSeAplicavel(cur);
          if (bg) elem.bgColor = bg;
        }
        if (tipo === 'label' || tipo === 'title' || tipo === 'subtitle') {
          const tc = corDeTextoSeAplicavel(cur);
          if (tc) elem.textColor = tc;
        }
        elementos.push(elem);

        // Por padrao, NAO desce em elementos capturados (evita duplicar texto interno).
        // EXCECOES: <label> que envolve campo, OU panel/card (container com fundo) — desce pra
        // pegar campos/textos internos, senao o panel fica vazio visualmente.
        if (isLabelWrapper || tipo === 'panel') {
          for (const child of Array.from(cur.children)) {
            if (child instanceof HTMLElement) fila.push(child);
          }
        }
        continue;
      }
    }

    // Desce nos filhos
    for (const child of Array.from(cur.children)) {
      if (child instanceof HTMLElement) fila.push(child);
    }
  }

  // Background da pagina: pega computed style do body (mais confiavel — sempre tem cor base
  // definida em styles.css, inclui CSS vars resolvidas). Se body tem gradient, retorna combinado.
  let pageBackground: string | undefined;
  const bodyCs = window.getComputedStyle(document.body);
  const bodyImg = bodyCs.backgroundImage;
  const bodyBg = bodyCs.backgroundColor;
  if (bodyImg && bodyImg !== 'none') {
    // Gradient + cor base: emite shorthand pra preservar ambos
    pageBackground = bodyBg && bodyBg !== 'rgba(0, 0, 0, 0)' && bodyBg !== 'transparent'
      ? `${bodyBg} ${bodyImg}`
      : bodyImg;
  } else if (bodyBg && bodyBg !== 'rgba(0, 0, 0, 0)' && bodyBg !== 'transparent') {
    pageBackground = bodyBg;
  }

  return {
    elements: elementos,
    idCounter: idCounter.v,
    containerWidth: Math.round(containerRect.width),
    containerHeight: Math.round(containerRect.height),
    ...(pageBackground ? { pageBackground } : {}),
  };
}

// Sobe na arvore ate achar o "container significativo" mais proximo do alvo.
// Logica: prioriza modais/dialogs (querem-se como bloco completo: header+body+footer).
// Quando nao tem modal, retorna o proprio elemento clicado.
export function acharContainerSignificativo(el: HTMLElement): HTMLElement {
  // 1) Modal: sobe ate .modal-conteudo / [role=dialog] / .modal-content
  let cur: HTMLElement | null = el;
  while (cur && cur !== document.body) {
    if (
      cur.classList.contains('modal-conteudo') ||
      cur.classList.contains('modal-content') ||
      cur.getAttribute('role') === 'dialog'
    ) {
      return cur;
    }
    cur = cur.parentElement;
  }
  // 2) Sem modal: retorna o elemento clicado
  return el;
}

// Codifica payload em base64 url-safe pra colar em ?import=...
export function codificarParaURL(payload: MockupPayload): string {
  const json = JSON.stringify(payload);
  // unescape/escape mantem suporte UTF-8 em base64 (acentos)
  return btoa(unescape(encodeURIComponent(json)));
}

// Abre o mockup-builder em nova aba ja preenchido.
// O mockup-builder.html mora em apps/web/public/, servido pelo Vite/Express como
// asset estatico em /mockup-builder.html. Antes passavamos o payload na URL via
// ?import=<base64>, mas o nginx limita URI a ~8KB e capturas grandes (414).
// Agora usamos localStorage como entrega: gera-se uma chave curta, payload e
// gravado em localStorage[mockup_import_<chave>], e a URL leva so ?import_key=<chave>.
// O mockup-builder.html le, parsea, e remove a chave em seguida.
export function abrirNoMockupBuilder(payload: MockupPayload): boolean {
  const chave = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  try {
    localStorage.setItem(`mockup_import_${chave}`, JSON.stringify(payload));
  } catch {
    return false;
  }
  const url = `/mockup-builder.html?import_key=${encodeURIComponent(chave)}`;
  const win = window.open(url, '_blank');
  return !!win;
}
